import { useEffect, useRef, useCallback } from 'react';
import { ChatMessageResponse } from '@/lib/api';
import { MessageItem } from './MessageItem';

interface MessageListProps {
  messages: ChatMessageResponse[];
  currentUserId: string | null;
  isAdmin?: boolean;
  loading: boolean;
  loadingMore: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
  onEditMessage?: (messageId: string, content: string) => void;
  onDeleteMessage?: (messageId: string) => void;
}

export function MessageList({
  messages,
  currentUserId,
  isAdmin,
  loading,
  loadingMore,
  hasMore,
  onLoadMore,
  onEditMessage,
  onDeleteMessage,
}: MessageListProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const prevMessageCountRef = useRef(0);

  // Scroll to bottom on initial load and when new messages arrive
  useEffect(() => {
    if (!loading && messages.length > 0) {
      if (prevMessageCountRef.current === 0) {
        // Initial load — instant scroll
        bottomRef.current?.scrollIntoView();
      } else if (messages.length > prevMessageCountRef.current) {
        // New message arrived — smooth scroll
        bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
      }
      prevMessageCountRef.current = messages.length;
    }
    // Reset when loading starts (channel switch)
    if (loading) {
      prevMessageCountRef.current = 0;
    }
  }, [loading, messages.length]);

  // Load more on scroll to top
  const handleScroll = useCallback(() => {
    if (!containerRef.current || !hasMore || loadingMore) return;
    if (containerRef.current.scrollTop < 100) {
      onLoadMore();
    }
  }, [hasMore, loadingMore, onLoadMore]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="animate-pulse space-y-4 w-full max-w-lg px-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-start gap-3">
              <div className="h-8 w-8 bg-muted rounded-full" />
              <div className="flex-1">
                <div className="h-3 bg-muted rounded w-20 mb-2" />
                <div className="h-4 bg-muted rounded w-48" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center text-muted-foreground">
          <p className="text-lg font-medium">No messages yet</p>
          <p className="text-sm mt-1">Start the conversation!</p>
        </div>
      </div>
    );
  }

  // Group messages by date
  let lastDate = '';

  return (
    <div ref={containerRef} onScroll={handleScroll} className="flex-1 overflow-y-auto px-4 py-2">
      {loadingMore && (
        <div className="flex justify-center py-2">
          <div className="h-5 w-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      )}
      {messages.map((message) => {
        const messageDate = new Date(message.createdAt).toLocaleDateString();
        const showDateSeparator = messageDate !== lastDate;
        lastDate = messageDate;

        return (
          <div key={message.messageId}>
            {showDateSeparator && (
              <div className="flex items-center gap-3 my-4">
                <div className="flex-1 h-px bg-border" />
                <span className="text-xs text-muted-foreground font-medium">
                  {new Date(message.createdAt).toLocaleDateString(undefined, {
                    weekday: 'long',
                    month: 'long',
                    day: 'numeric',
                  })}
                </span>
                <div className="flex-1 h-px bg-border" />
              </div>
            )}
            <MessageItem
              message={message}
              isOwnMessage={message.senderId === currentUserId}
              isAdmin={isAdmin}
              onEdit={onEditMessage}
              onDelete={onDeleteMessage}
            />
          </div>
        );
      })}
      <div ref={bottomRef} />
    </div>
  );
}
